import type Liff from '@line/liff'

let initPromise: Promise<typeof Liff> | null = null

/*
 * initLiff()（= liff.init() の実行）が呼ばれたことを getLiff() 側へ知らせる合図
 * getLiff() が先に呼ばれても initLiff() の実行を待てるようにするためのもの
 * initLiff() は 1 回だけ呼ばれる前提
 */
let signalInitCalled: (() => void) | null = null
const initCalled = new Promise<void>(resolve => {
  signalInitCalled = resolve
})

// initLiff() がいつまでも呼ばれないときに、待ち続けないための制限時間
const GET_LIFF_TIMEOUT_MS = 5_000

/*
 * LINE の準備（liff.init()）に失敗したままかどうか
 *
 * 失敗すると initPromise は空に戻るため、それだけでは「まだ準備していない」のか
 * 「準備に失敗した」のかが区別できない。呼び出し側が失敗だけを見分けられるようにする
 */
let initFailed = false

export const hasLiffInitFailed = (): boolean => initFailed

const _init = async (liffId: string): Promise<typeof Liff> => {
  const { default: liff } = await import('@line/liff')
  await liff.init({ liffId })
  return liff
}

export const initLiff = (liffId: string): Promise<typeof Liff> => {
  if (!initPromise) {
    // やり直しの場合に、前回の失敗が残らないようにする
    initFailed = false
    initPromise = _init(liffId).catch(err => {
      initPromise = null
      initFailed = true
      throw err
    })
    signalInitCalled?.()
  }
  return initPromise
}

export const getLiff = async (): Promise<typeof Liff> => {
  if (!initPromise) {
    // まだ initLiff() が呼ばれていないだけの可能性が高いため、すぐにエラーにせず
    // initLiff() の呼び出しを少し待つ。制限時間を過ぎても呼ばれなければエラーにする
    let timer: ReturnType<typeof setTimeout> | undefined
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(
        () =>
          reject(new Error('LIFF is not initialized. Call initLiff() first.')),
        GET_LIFF_TIMEOUT_MS
      )
    })
    try {
      await Promise.race([initCalled, timeout])
    } finally {
      if (timer) clearTimeout(timer)
    }
  }
  // liff.init() に失敗した直後などの、例外的なケース
  if (!initPromise) {
    throw new Error('LIFF is not initialized. Call initLiff() first.')
  }
  return initPromise
}
